// ============================================
// UI State Persistence Utilities
// ============================================
// Persists a small, JSON-serializable slice of the app's UI state to
// localStorage under a single versioned key so user selections and display
// settings survive a browser refresh. Mirrors the defensive try/catch style
// used by the patient-data cache in hooks/usePatientData.js (localStorage can
// throw on read/parse and on write when the quota is exceeded or storage is
// disabled).

export const UI_STATE_STORAGE_KEY = 'neuronova_ui_state_v1';

// Read the persisted UI state. Always returns a plain object so callers can
// safely destructure with `?? default` fallbacks.
export const loadPersistedState = () => {
    try {
        const raw = localStorage.getItem(UI_STATE_STORAGE_KEY);
        if (!raw) {
            return {};
        }
        const parsed = JSON.parse(raw);
        return parsed && typeof parsed === 'object' ? parsed : {};
    } catch (error) {
        console.warn('Error reading persisted UI state:', error);
        return {};
    }
};

// Merge `partial` into the currently persisted state and write it back. Merging
// (rather than replacing) lets independent components (e.g. NeuroViewer and the
// PatientZScoresProvider) each persist their own slice without clobbering the
// other's keys.
export const persistState = (partial) => {
    if (!partial || typeof partial !== 'object') {
        return;
    }
    try {
        const current = loadPersistedState();
        const next = { ...current, ...partial };
        localStorage.setItem(UI_STATE_STORAGE_KEY, JSON.stringify(next));
    } catch (error) {
        console.warn('Error persisting UI state:', error);
    }
};

// Remove the persisted UI state entirely (used by "Reset settings").
export const clearPersistedState = () => {
    try {
        localStorage.removeItem(UI_STATE_STORAGE_KEY);
    } catch (error) {
        console.warn('Error clearing persisted UI state:', error);
    }
};
